import { useCallback, useEffect, useState } from "react";
import { FileStack, Printer, X } from "lucide-react";
import api, { fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminManifests() {
  const [manifests, setManifests] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => api.get("/ops/manifests").then(({ data }) => setManifests(data)).catch(() => setManifests([])), []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (m) => {
    try {
      const { data } = await api.get(`/ops/manifest/${m.manifest_id}`);
      setDetail(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="admin-manifests-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Manifests</h1>
      <p className="text-sm text-slate-500">All customer manifests — the same backend records customers see. Verify AWBs, courier and status before handover.</p>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="admin-manifests-table">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">Manifest</th><th className="px-4 py-3.5">Customer</th><th className="px-4 py-3.5">Courier</th>
              <th className="px-4 py-3.5">Packages</th><th className="px-4 py-3.5">Status</th><th className="px-4 py-3.5">Created</th><th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {(manifests || []).map((m) => (
              <tr key={m.manifest_id} className="border-t border-slate-100" data-testid={`admin-manifest-row-${m.manifest_id}`}>
                <td className="mono px-5 py-3 font-semibold text-shipany-navy">{m.manifest_id}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{m.customer_email || "—"}</td>
                <td className="px-4 py-3 text-xs font-semibold">{m.courier_code}</td>
                <td className="mono px-4 py-3">{m.count}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold uppercase text-emerald-600">{m.status}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(m.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openDetail(m)} data-testid={`admin-manifest-view-${m.manifest_id}`}
                      className="flex items-center gap-1.5 rounded-lg bg-shipany-navy px-3.5 py-1.5 text-xs font-bold text-white hover:bg-shipany-blue">
                      <FileStack className="h-3.5 w-3.5" /> View AWBs
                    </button>
                    <a href={`${API_BASE}/api/ops/manifest/${m.manifest_id}/document`} target="_blank" rel="noreferrer" data-testid={`admin-manifest-print-${m.manifest_id}`}
                      className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:border-shipany-blue hover:text-shipany-blue">
                      <Printer className="h-3.5 w-3.5" /> Print
                    </a>
                  </div>
                </td>
              </tr>
            ))}
            {manifests && manifests.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-center text-slate-400">No manifests created yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-manifest-detail-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Manifest {detail.manifest_id}</h2>
              <button onClick={() => setDetail(null)} data-testid="admin-manifest-detail-close" className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Customer {detail.customer_email || "—"} · {detail.count} package(s) · courier {detail.courier_code} · created {fmtDateTime(detail.created_at)} · status {detail.status}
            </p>
            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[560px] text-sm">
                <thead><tr className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-widest text-slate-400"><th className="px-4 py-2.5">AWB</th><th className="px-4 py-2.5">Order</th><th className="px-4 py-2.5">Destination</th><th className="px-4 py-2.5">Weight</th><th className="px-4 py-2.5">Status</th></tr></thead>
                <tbody>
                  {(detail.shipments || []).map((s) => (
                    <tr key={s.shipment_id} className="border-t border-slate-100" data-testid={`admin-manifest-awb-${s.awb}`}>
                      <td className="mono px-4 py-2.5 font-semibold text-shipany-blue">{s.awb}</td>
                      <td className="mono px-4 py-2.5 text-xs">{s.order_number || "—"}</td>
                      <td className="px-4 py-2.5 text-xs">{s.destination_city} — {s.delivery_pincode}</td>
                      <td className="mono px-4 py-2.5 text-xs">{s.weight} kg</td>
                      <td className="px-4 py-2.5 text-xs">{s.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <a href={`${API_BASE}/api/ops/manifest/${detail.manifest_id}/document`} target="_blank" rel="noreferrer" data-testid="admin-manifest-detail-print"
              className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-shipany-navy py-3 text-sm font-bold text-white hover:bg-shipany-blue">
              <Printer className="h-4 w-4" /> Print / Save PDF
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
